import BaseButton from '@/components/base/BaseButton';
import {
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { ConfigProvider, Modal, theme, type ModalFuncProps } from 'antd';
import enUS from 'antd/locale/en_US';
import { useContext, useRef, useState } from 'react';

export interface BaseConfirmProps {
  config: ModalFuncProps;
  open: boolean;
  onFinish: (confirmed: boolean) => void;
  onHidden: () => void;
}

/** 紧凑确认外壳，保留命令式回调协议，避免使用表单弹窗的分区布局。 */
export default function BaseConfirm({
  config,
  open,
  onFinish,
  onHidden,
}: BaseConfirmProps) {
  const { locale } = useContext(ConfigProvider.ConfigContext);
  const { token } = theme.useToken();
  const [loading, setLoading] = useState({ ok: false, cancel: false });
  const running = useRef({ ok: false, cancel: false });
  const {
    title,
    content,
    onOk,
    onCancel,
    afterClose: _afterClose,
    open: _open,
    type: _type,
    direction: _direction,
    autoFocusButton,
    focusable,
    okCancel = true,
    icon = <ExclamationCircleOutlined aria-hidden />,
    width = 448,
    footer,
    okButtonProps,
    cancelButtonProps,
    className,
    ...props
  } = config;

  const run = async (kind: 'ok' | 'cancel') => {
    if (running.current[kind]) return;
    const action = kind === 'ok' ? onOk : onCancel;
    const close = () => onFinish(kind === 'ok');
    if (!action) {
      close();
      return;
    }
    running.current[kind] = true;
    try {
      // 带 close 参数的回调由调用方决定何时关闭；Promise 成功才自动关闭。
      const result: unknown = action.length ? action(close) : action();
      if (
        result &&
        typeof (result as PromiseLike<unknown>).then === 'function'
      ) {
        setLoading((value) => ({ ...value, [kind]: true }));
        await result;
        close();
      } else if (!action.length) {
        close();
      }
    } catch (error) {
      // 请求层负责用户错误反馈；确认失败保留弹窗与输入，错误仍留在控制台供定位。
      console.error('确认操作失败，弹窗保留供重试', error);
    } finally {
      running.current[kind] = false;
      setLoading((value) => ({ ...value, [kind]: false }));
    }
  };

  const okText = props.okText ?? locale?.Modal?.okText ?? enUS.Modal?.okText;
  const cancelText =
    props.cancelText ?? locale?.Modal?.cancelText ?? enUS.Modal?.cancelText;
  const okButton = (
    <BaseButton
      type={props.okType === 'danger' ? 'primary' : props.okType ?? 'primary'}
      danger={props.okType === 'danger'}
      loading={loading.ok}
      icon={<CheckOutlined aria-hidden />}
      onClick={() => void run('ok')}
      {...okButtonProps}
    >
      {okText}
    </BaseButton>
  );
  const cancelButton = (
    <BaseButton
      loading={loading.cancel}
      icon={<CloseOutlined aria-hidden />}
      onClick={() => void run('cancel')}
      {...cancelButtonProps}
    >
      {cancelText}
    </BaseButton>
  );
  const defaultFooter = (
    <>
      {okCancel && cancelButton}
      {okButton}
    </>
  );
  const customFooter =
    typeof footer === 'function'
      ? footer(defaultFooter, {
          OkBtn: () => okButton,
          CancelBtn: () => cancelButton,
        })
      : footer;

  return (
    <Modal
      {...props}
      open={open}
      title={
        <div className="base-confirm-heading">
          {icon !== null && icon !== false && (
            <span
              className="base-confirm-icon"
              style={
                props.okType === 'danger' || okButtonProps?.danger
                  ? { color: token.colorError }
                  : undefined
              }
              aria-hidden
            >
              {icon}
            </span>
          )}
          <span>{title}</span>
        </div>
      }
      width={width}
      centered
      destroyOnHidden
      className={['base-confirm', className].filter(Boolean).join(' ')}
      mask={config.mask ?? { closable: config.maskClosable ?? false }}
      focusable={{
        autoFocusButton: autoFocusButton === undefined ? 'ok' : autoFocusButton,
        ...focusable,
      }}
      confirmLoading={loading.ok}
      cancelButtonProps={{ loading: loading.cancel, ...cancelButtonProps }}
      okButtonProps={okButtonProps}
      onOk={() => void run('ok')}
      onCancel={() => void run('cancel')}
      footer={footer === null ? null : customFooter ?? defaultFooter}
      afterClose={onHidden}
    >
      {content}
    </Modal>
  );
}
